import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Gift, User, Mail, DollarSign, MessageSquare, Search, Check, Users, Phone, Edit3, AlertCircle, Tag } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { GiftCardService } from '../services/giftCardService';
import { GiftCardSettings } from '../services/giftCardSettingsService';
import GiftCardWrapper from './GiftCardWrapper';
import SelectCustomerModal from './manage/SelectCustomerModal';
import EditCustomerModal from './manage/EditCustomerModal';
import RecordPaymentModal from './RecordPaymentModal';
import { Customer } from '../lib/customers';
import { getPersonInitials } from '../utils/personInitials';
import { sendGmailEmail } from '../lib/email';
import { generateGiftCardEmailHTML } from '../services/giftCardEmailService';
import { generateGiftCardImage } from '../services/giftCardImageService';
import { generateGiftCardImageCanvas } from '../services/giftCardImageServiceCanvas';
import { GiftCardEmailSettingsService } from '../services/giftCardEmailSettingsService';
import { GiftCardPromotionService, PromotionBonus } from '../services/giftCardPromotionService';

interface CreateGiftCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function CreateGiftCardModal({ isOpen, onClose, onSuccess }: CreateGiftCardModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [selectedDesign, setSelectedDesign] = useState<GiftCardSettings | null>(null);
  const [amount, setAmount] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [notes, setNotes] = useState('');
  
  // Design selection
  const [designs, setDesigns] = useState<GiftCardSettings[]>([]);
  const [loadingDesigns, setLoadingDesigns] = useState(true);
  
  // Customer selection
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isSelectCustomerModalOpen, setIsSelectCustomerModalOpen] = useState(false);
  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  
  // Recipient section toggle
  const [showRecipientSection, setShowRecipientSection] = useState(false);
  
  // Promotion state
  const [promotionBonus, setPromotionBonus] = useState<PromotionBonus | null>(null);
  
  // Payment modal
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [createdPaymentId, setCreatedPaymentId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadDesigns();
      resetForm();
    }
  }, [isOpen, selectedBusiness?.id]);

  const loadDesigns = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoadingDesigns(true);
      const { data, error } = await supabase
        .from('gift_card_settings')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDesigns(data || []);
      
      // Auto-select first design if available
      if (data && data.length > 0) {
        setSelectedDesign(data[0]);
      }
    } catch (error) {
      console.error('Error loading designs:', error);
      toast.error('Failed to load gift card designs');
    } finally {
      setLoadingDesigns(false);
    }
  };

  const resetForm = () => {
    setSelectedDesign(null);
    setAmount('');
    setRecipientName('');
    setRecipientEmail('');
    setCustomMessage('');
    setNotes('');
    setSelectedCustomer(null);
    setShowRecipientSection(false);
    setPromotionBonus(null);
    setError(null);
  };

  // Check for active promotions when amount changes
  useEffect(() => {
    const checkPromotion = async () => {
      if (!selectedBusiness?.id || !amount) {
        setPromotionBonus(null);
        return;
      }

      const amountNum = parseFloat(amount);
      if (isNaN(amountNum) || amountNum <= 0) {
        setPromotionBonus(null);
        return;
      }

      try {
        const bonus = await GiftCardPromotionService.applyPromotion(selectedBusiness.id, amountNum);
        setPromotionBonus(bonus);
      } catch (error) {
        console.error('Error checking promotion:', error);
        setPromotionBonus(null);
      }
    };

    checkPromotion();
  }, [amount, selectedBusiness?.id]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      resetForm();
    }
  };

  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsSelectCustomerModalOpen(false);
  };

  const handleCreateGiftCardAfterPayment = async (paymentId?: string) => {
    if (!selectedBusiness?.id || !selectedDesign || !selectedCustomer) {
      setError('Missing required information to create gift card');
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError('Invalid gift card amount');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Create the gift card with promotion if applicable
      const result = await GiftCardService.createGiftCard({
        business_id: selectedBusiness.id,
        design_id: selectedDesign.id!,
        amount: amountNum,
        purchaser_customer_id: selectedCustomer.id,
        recipient_name: recipientName || undefined,
        recipient_email: recipientEmail || undefined,
        custom_message: customMessage || undefined,
        notes: notes || undefined,
        promotion_id: promotionBonus?.promotion.id,
        bonus_amount: promotionBonus?.bonusAmount
      });

      if (!result.success || !result.giftCard) {
        setError(result.error || 'Failed to create gift card');
        return;
      }

      // Link payment to gift card if payment ID was provided
      if (paymentId && result.giftCard.id) {
        try {
          const { error: updateError } = await supabase
            .from('payments')
            .update({ gift_card_id: result.giftCard.id })
            .eq('id', paymentId);

          if (updateError) {
            console.error('Failed to link payment to gift card:', updateError);
            // Don't fail the whole process if linking fails
          } else {
            console.log('✅ Payment linked to gift card successfully');
          }
        } catch (linkError) {
          console.error('Error linking payment to gift card:', linkError);
          // Continue anyway - gift card was created successfully
        }
      }

      // Send gift card email
      try {
        // Determine recipient email: recipient if exists, otherwise purchaser
        const emailToSendTo = recipientEmail && recipientEmail.trim() 
          ? recipientEmail.trim() 
          : selectedCustomer.email;

        if (emailToSendTo) {
          // Load full design settings if needed
          const fullDesign = selectedDesign.id 
            ? await (async () => {
                const { data } = await supabase
                  .from('gift_card_settings')
                  .select('*')
                  .eq('id', selectedDesign.id)
                  .single();
                return data || selectedDesign;
              })()
            : selectedDesign;

          // Determine if this is for recipient or purchaser
          const isForRecipient = !!(recipientEmail && recipientEmail.trim());

          // Load email settings
          const emailSettings = await GiftCardEmailSettingsService.getEmailSettings(selectedBusiness.id);

          // Generate gift card image using Canvas API (more reliable)
          console.log('📸 Generating gift card image using Canvas API...');
          let giftCardImageDataUrl: string | undefined;
          try {
            // Try Canvas API approach first (more reliable)
            giftCardImageDataUrl = await generateGiftCardImageCanvas({
              giftCardCode: result.giftCard.code,
              design: fullDesign as GiftCardSettings,
              businessLogoUrl: selectedBusiness.booking_settings?.logo_url,
              recipientName: recipientName || undefined,
              width: 600 // Good size for email
            });
            
            // Verify the image was generated
            if (!giftCardImageDataUrl || giftCardImageDataUrl.length < 100) {
              console.error('❌ Generated image data URL is invalid or too short');
              giftCardImageDataUrl = undefined;
            } else {
              console.log('✅ Gift card image generated successfully using Canvas API, length:', giftCardImageDataUrl.length);
            }
          } catch (canvasError) {
            console.warn('⚠️ Canvas API approach failed, trying html2canvas fallback:', canvasError);
            // Fallback to html2canvas if Canvas API fails
            try {
              giftCardImageDataUrl = await generateGiftCardImage({
                giftCardCode: result.giftCard.code,
                design: fullDesign as GiftCardSettings,
                businessLogoUrl: selectedBusiness.booking_settings?.logo_url,
                recipientName: recipientName || undefined,
                width: 600
              });
              
              if (!giftCardImageDataUrl || giftCardImageDataUrl.length < 100) {
                console.error('❌ Fallback image generation also failed');
                giftCardImageDataUrl = undefined;
              } else {
                console.log('✅ Gift card image generated using html2canvas fallback');
              }
            } catch (imageError) {
              console.error('❌ Both image generation methods failed:', imageError);
              giftCardImageDataUrl = undefined;
            }
          }

          // Generate email HTML
          const emailHTML = generateGiftCardEmailHTML({
            giftCardCode: result.giftCard.code,
            amount: amountNum,
            design: fullDesign as GiftCardSettings,
            businessName: selectedBusiness.name,
            businessLogoUrl: selectedBusiness.booking_settings?.logo_url,
            recipientName: recipientName || undefined,
            customMessage: customMessage || undefined,
            isForRecipient: isForRecipient,
            giftCardImageDataUrl: giftCardImageDataUrl,
            emailSettings: emailSettings || undefined
          });

          // Send email
          const emailResult = await sendGmailEmail({
            businessId: selectedBusiness.id,
            to: emailToSendTo,
            subject: isForRecipient
              ? `You've Received a Gift Card from ${selectedBusiness.name}!`
              : `Your Gift Card from ${selectedBusiness.name}`,
            body: emailHTML
          });

          if (emailResult.success) {
            console.log('✅ Gift card email sent successfully');
            toast.success(`Gift card created and emailed! Code: ${result.giftCard.code}`);
          } else {
            console.error('Failed to send gift card email:', emailResult.error);
            toast.success(`Gift card created! Code: ${result.giftCard.code} (Email failed to send)`);
          }
        } else {
          console.warn('No email address available to send gift card');
          toast.success(`Gift card created! Code: ${result.giftCard.code} (No email address)`);
        }
      } catch (emailError) {
        console.error('Error sending gift card email:', emailError);
        // Don't fail the whole process if email fails
        toast.success(`Gift card created! Code: ${result.giftCard.code} (Email failed to send)`);
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error creating gift card:', error);
      setError('Failed to create gift card. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedBusiness?.id || !selectedDesign) {
      setError('Please select a design');
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (!selectedCustomer) {
      setError('Please select a customer');
      return;
    }

    // Open payment modal instead of creating gift card directly
    setIsPaymentModalOpen(true);
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`fixed inset-0 z-[100010] transition-opacity duration-200 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
        onTransitionEnd={handleTransitionEnd}
      />

      {/* Modal Content */}
      <div
        className={`fixed inset-y-0 right-0 z-[100050] w-full md:w-[700px] lg:w-[800px] bg-white shadow-2xl transform transition-transform duration-200 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
                Sell Gift Card
              </h2>
            </div>
          </div>

          {/* Content */}
          <form 
            onSubmit={handleSubmit} 
            className="flex-1 overflow-y-auto bg-white"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="py-6 px-6 space-y-6">
            
            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                <div className="flex items-center">
                  <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                  <p>{error}</p>
                </div>
              </div>
            )}
            
            {/* Design Selection */}
            <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select Card Design *
              </label>
              {loadingDesigns ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : designs.length === 0 ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <p className="text-sm text-yellow-800">
                    No gift card designs found. Please create a design first in Settings.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {designs.map((design) => (
                    <div
                      key={design.id}
                      onClick={() => setSelectedDesign(design)}
                      className={`relative border-2 rounded-xl p-3 cursor-pointer transition-all active:scale-[0.98] touch-manipulation ${
                        selectedDesign?.id === design.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200/60 hover:border-gray-300'
                      }`}
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      {selectedDesign?.id === design.id && (
                        <div className="absolute top-2 right-2 bg-blue-600 rounded-full p-1">
                          <Check className="h-3 w-3 text-white" />
                        </div>
                      )}
                      <div className="mb-2">
                        <GiftCardWrapper
                          settings={design}
                          businessLogoUrl={selectedBusiness?.booking_settings?.logo_url}
                          showSampleData={true}
                          maxWidth="100%"
                        />
                      </div>
                      <p className="text-sm font-medium text-gray-900 text-center">
                        {design.card_title}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Amount */}
            <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gift Card Amount *
              </label>
              <div className="relative">
                <DollarSign className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                  style={{ fontSize: '16px' }}
                  required
                />
              </div>

              {/* Active Promotion Display */}
              {promotionBonus && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <Tag className="h-5 w-5 text-green-600 mt-0.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-green-900">
                          {promotionBonus.promotion.name}
                        </span>
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                          ACTIVE
                        </span>
                      </div>
                      {promotionBonus.promotion.description && (
                        <p className="text-xs text-green-700 mb-2">{promotionBonus.promotion.description}</p>
                      )}
                      <div className="flex items-center justify-between text-sm">
                        <div>
                          <div className="text-gray-600">Purchase Amount:</div>
                          <div className="text-gray-600">Bonus Amount:</div>
                          <div className="font-semibold text-green-900 border-t border-green-200 mt-1 pt-1">
                            Total Card Value:
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-gray-900">${parseFloat(amount).toFixed(2)}</div>
                          <div className="text-green-600 font-medium">+${promotionBonus.bonusAmount.toFixed(2)}</div>
                          <div className="font-bold text-green-900 border-t border-green-200 mt-1 pt-1">
                            ${promotionBonus.totalAmount.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Purchaser Information */}
            <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-6">
              <h3 className="text-sm font-medium text-gray-700 mb-4">Purchaser Information</h3>
              
              {/* Customer Selection */}
              {selectedCustomer ? (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Selected Customer
                  </label>
                  <div className="bg-gray-50 rounded-[12px] border border-gray-200/60 p-4">
                    <div className="flex items-center space-x-3">
                      <div className="h-12 w-12 rounded-full bg-gradient-to-b from-gray-300 to-gray-400 shadow-sm flex items-center justify-center text-white font-medium text-sm flex-shrink-0 uppercase">
                        {getPersonInitials(selectedCustomer.first_name, selectedCustomer.last_name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-base font-medium text-gray-900">
                          {selectedCustomer.first_name} {selectedCustomer.last_name}
                        </div>
                        <div className="flex flex-col space-y-1 text-sm text-gray-600 mt-1">
                          {selectedCustomer.email && (
                            <div className="flex items-center space-x-2">
                              <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              <span className="truncate">{selectedCustomer.email}</span>
                            </div>
                          )}
                          {selectedCustomer.phone && (
                            <div className="flex items-center space-x-2">
                              <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              <span>{selectedCustomer.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => setIsEditCustomerModalOpen(true)}
                          className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700 font-semibold px-3 py-1.5 rounded-xl hover:bg-blue-50 active:bg-blue-100 active:scale-95 transition-all duration-150 touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <Edit3 className="h-3.5 w-3.5" />
                          <span>Edit</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedCustomer(null);
                          }}
                          className="flex items-center space-x-1 text-sm text-red-600 hover:text-red-700 font-semibold px-3 py-1.5 rounded-xl hover:bg-red-50 active:bg-red-100 active:scale-95 transition-all duration-150 touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <X className="h-3.5 w-3.5" />
                          <span>Clear</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mb-4">
                  <button
                    type="button"
                    onClick={() => setIsSelectCustomerModalOpen(true)}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-500 hover:bg-blue-50 active:bg-blue-100 active:scale-[0.98] transition-all duration-150 touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Users className="h-5 w-5 text-gray-400" />
                    <span className="text-sm font-semibold text-gray-600">Select Customer</span>
                  </button>
                </div>
              )}

            </div>

            {/* Recipient Information */}
            <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-6">
              {!showRecipientSection ? (
                <button
                  type="button"
                  onClick={() => setShowRecipientSection(true)}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-500 hover:bg-blue-50 active:bg-blue-100 active:scale-[0.98] transition-all duration-150 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <User className="h-5 w-5 text-gray-400" />
                  <span className="text-sm font-semibold text-gray-600">Add Recipient (Optional)</span>
                </button>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-700">Recipient Information</h3>
                    <button
                      type="button"
                      onClick={() => {
                        setShowRecipientSection(false);
                        setRecipientName('');
                        setRecipientEmail('');
                        setCustomMessage('');
                      }}
                      className="text-sm text-red-600 hover:text-red-700 font-semibold active:scale-95 transition-all duration-150 touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      Remove
                    </button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Recipient Name
                      </label>
                      <input
                        type="text"
                        value={recipientName}
                        onChange={(e) => setRecipientName(e.target.value)}
                        placeholder="Enter recipient name"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Recipient Email
                      </label>
                      <input
                        type="email"
                        value={recipientEmail}
                        onChange={(e) => setRecipientEmail(e.target.value)}
                        placeholder="recipient@example.com"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Custom Message
                      </label>
                      <textarea
                        value={customMessage}
                        onChange={(e) => setCustomMessage(e.target.value)}
                        placeholder="Add a personal message for the recipient..."
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 resize-none"
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Internal Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any internal notes..."
                rows={2}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 resize-none"
                style={{ fontSize: '16px' }}
              />
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 mt-6">
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 active:bg-gray-200 active:scale-[0.98] font-semibold transition-all duration-150 touch-manipulation disabled:opacity-50"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !selectedDesign || !amount}
                  className="flex items-center space-x-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white rounded-xl font-semibold transition-all duration-150 active:scale-[0.98] touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <Gift className="h-4 w-4" />
                      <span>Sell Gift Card</span>
                    </>
                  )}
                </button>
              </div>
            </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      
      {/* Select Customer Modal */}
      <SelectCustomerModal
        isOpen={isSelectCustomerModalOpen}
        onClose={() => setIsSelectCustomerModalOpen(false)}
        onSelect={handleCustomerSelect}
      />

      {/* Edit Customer Modal */}
      <EditCustomerModal
        isOpen={isEditCustomerModalOpen}
        onClose={() => setIsEditCustomerModalOpen(false)}
        onSuccess={(updatedCustomer) => {
          setIsEditCustomerModalOpen(false);
          setSelectedCustomer(updatedCustomer);
        }}
        customer={selectedCustomer}
      />

      {/* Payment Modal */}
      <RecordPaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => {
          setIsPaymentModalOpen(false);
          setCreatedPaymentId(null);
        }}
        onSuccess={async (paymentId?: string) => {
          // Store payment ID if provided
          if (paymentId) {
            setCreatedPaymentId(paymentId);
          }
          
          // Create gift card after payment success
          await handleCreateGiftCardAfterPayment(paymentId);
        }}
        businessId={selectedBusiness?.id || ''}
        bookingTotal={parseFloat(amount) || 0}
        amountPaid={0}
        // No bookingId or giftCardId yet - will link gift card after creation
      />
    </>
  );
}

